import React, { useEffect, useMemo, useState } from 'react';
import { FileEarmark, Paperclip } from 'react-bootstrap-icons';
import { toast } from 'sonner';
import AttachSupportingDocuments, {
  getDocumentUploadState,
  getPersistedSupportingDocuments
} from '../../newOrder/Components/AttachSupportingDocuments';
import { makeRESTApiRequest } from '../../../helpers/api';
import { ADMIN_ROLE, ENDPOINTS, PROCESSOR_ROLE } from '../../../helpers/constants';

const formatFileSize = bytes => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)}KB`;
  const mb = bytes / (1024 * 1024);
  return mb >= 10 ? `${Math.round(mb)}MB` : `${mb.toFixed(1)}MB`;
};

const chipClassName =
  'd-inline-flex align-items-center rounded px-2 py-1 bg-grey text-dark text-decoration-none midFont mr-2 mb-1';

const isPersistedDocument = doc => getDocumentUploadState(doc) === 'uploaded';

const ClientAttachmentsBar = ({
  documents = [],
  onDocumentsChange,
  folderId = null,
  orderType,
  serviceId,
  disabled = false
}) => {
  const role = useMemo(() => localStorage.getItem('user-role'), []);
  const canUpload = [ADMIN_ROLE, PROCESSOR_ROLE].includes(role);
  const [localDocuments, setLocalDocuments] = useState(documents);

  useEffect(() => {
    setLocalDocuments(documents || []);
  }, [documents]);

  const persistedDocuments = useMemo(() => (localDocuments || []).filter(isPersistedDocument), [localDocuments]);

  const handleDocumentsChange = documentsOrUpdater => {
    setLocalDocuments(prev => {
      const current = Array.isArray(prev) ? prev : [];
      return typeof documentsOrUpdater === 'function' ? documentsOrUpdater(current) : documentsOrUpdater;
    });
  };

  const handleBatchUploadComplete = completedDocuments => {
    if (!serviceId || !completedDocuments?.length) return;

    const existing = getPersistedSupportingDocuments(documents);
    const existingIds = new Set(existing.map(doc => doc.id));
    const toAdd = completedDocuments.filter(doc => doc.id && !existingIds.has(doc.id));

    if (!toAdd.length) return;

    const mergedSupportingDocuments = [...existing, ...toAdd];

    makeRESTApiRequest({
      endpoint: ENDPOINTS.SERVICE_WITH_ID(serviceId),
      method: 'PUT',
      requestBody: { supportingDocuments: mergedSupportingDocuments }
    }).then(({ error, response }) => {
      if (error) {
        toast.error(error);
        return;
      }
      const nextDocuments = response?.supportingDocuments ?? mergedSupportingDocuments;
      onDocumentsChange?.(nextDocuments);
      setLocalDocuments(nextDocuments);
    });
  };

  if (!persistedDocuments.length || !canUpload) {
    return null;
  }

  return (
    <div className="client-attachments-bar d-flex flex-column border rounded p-2 bg-blue-light w-100">
      <div className="d-flex flex-column flex-lg-row align-items-start align-items-lg-center flex-wrap">
        <div className="d-inline-flex align-items-center flex-shrink-0 mb-2 mb-lg-0 mr-lg-3">
          <Paperclip size={18} className="text-info flex-shrink-0 mr-2" aria-hidden />
          <span className="font-weight-bold text-dark midFont text-nowrap mr-2">Client Attachments</span>
          <span className="badge badge-success p-1 px-2">NEW</span>
        </div>

        {persistedDocuments.length > 0 && (
          <div className="d-flex flex-wrap align-items-center flex-grow-1 mb-2 mb-lg-0">
            {persistedDocuments.map(doc => {
              const href = doc.id ? `https://drive.google.com/file/d/${doc.id}/view` : undefined;
              const chip = (
                <>
                  <FileEarmark size={14} className="text-muted flex-shrink-0 mr-2" aria-hidden />
                  <span className="text-truncate">{doc.filename}</span>
                  {doc.size != null && (
                    <span className="text-muted small ml-2 flex-shrink-0">{formatFileSize(doc.size)}</span>
                  )}
                </>
              );

              return href ? (
                <a
                  key={doc.id || doc.filename}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={chipClassName}
                  title={doc.filename}
                >
                  {chip}
                </a>
              ) : (
                <span key={doc.id || doc.filename} className={chipClassName} title={doc.filename}>
                  {chip}
                </span>
              );
            })}
          </div>
        )}

        {canUpload && (
          <div className="d-flex align-items-center flex-shrink-0 mb-2 mb-lg-0 mr-lg-3">
            <AttachSupportingDocuments
              variant="toolbar"
              documents={localDocuments}
              onDocumentsChange={handleDocumentsChange}
              syncOnEachUpload={false}
              onBatchUploadComplete={handleBatchUploadComplete}
              folderId={folderId}
              orderType={orderType}
              disabled={disabled}
              documentsListFilter={doc => doc.status === 'pending' || doc.status === 'error'}
            />
          </div>
        )}

        <p className="text-muted small font-italic mb-0 ml-lg-auto flex-shrink-0">
          Uploaded by client at order submission
        </p>
      </div>
    </div>
  );
};

export default ClientAttachmentsBar;
